import { useCallback, useEffect, useMemo, useState } from 'react';
import { publicApi } from '../services/api';
import {
  getCachedCatalogProducts,
  loadCatalogProducts,
} from '../services/publicCatalogCache';
import { SkeletonLoader } from '../components/ui';
import CatalogProductGrid from '../components/catalog/CatalogProductGrid';
import DynamicCatalogBanner from '../components/catalog/DynamicCatalogBanner';
import CatalogLifestyleBlock from '../components/catalog/CatalogLifestyleBlock';
import CatalogLifestyleVideoSection from '../components/catalog/CatalogLifestyleVideoSection';
import { CatalogImageRotationProvider } from '../components/catalog/CatalogImageRotationContext';
import { buildCatalogFeed } from '../utils/buildCatalogFeed';

const getCatalogProductImages = (product = {}) =>
  Array.isArray(product.images) && product.images.length > 0
    ? product.images.filter(Boolean)
    : [product.imageUrl || product.image_url || product.image].filter(Boolean);

const Catalog = () => {
  const [products, setProducts] = useState(() => getCachedCatalogProducts()?.data || []);
  const [loading, setLoading] = useState(() => !getCachedCatalogProducts()?.data?.length);
  const [error, setError] = useState('');
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  const loadProducts = useCallback(async () => {
    let isMounted = true;

    try {
      const cached = getCachedCatalogProducts();
      if (!cached?.data?.length) {
        setLoading(true);
      }
      setError('');

      const result = await loadCatalogProducts(async () => {
        const response = await publicApi.get('/products');
        return Array.isArray(response.data?.data) ? response.data.data : [];
      });

      if (!isMounted) {
        return () => {};
      }

      setProducts(result.data);
      setLoading(false);

      if (result.revalidate) {
        void result.revalidate
          .then((nextProducts) => {
            if (!isMounted) {
              return;
            }
            setProducts(nextProducts);
            setError('');
          })
          .catch((err) => {
            if (!isMounted) {
              return;
            }
            console.error('Failed to refresh products:', err);
          });
      }
    } catch (err) {
      console.error('Failed to fetch products:', err);
      if (isMounted) {
        setProducts((currentProducts) => currentProducts);
        setError((currentError) =>
          products.length > 0
            ? currentError
            : (
                err.response?.data?.message ||
                'We could not load the catalog right now. Please refresh or try again shortly.'
              )
        );
      }
    } finally {
      if (isMounted) {
        setLoading(false);
      }
    }
    
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let cleanup;
    void (async () => {
      cleanup = await loadProducts();
    })();

    return () => {
      if (typeof cleanup === 'function') {
        cleanup();
      }
    };
  }, [loadProducts]);

  const hasEmptyCatalog = !loading && !error && products.length === 0;
  const feedBlocks = useMemo(() => buildCatalogFeed(products), [products]);
  const hasRotatingCatalogImages = useMemo(
    () => products.some((product) => getCatalogProductImages(product).length > 1),
    [products]
  );
  const hasAttentionLoop = useMemo(
    () => feedBlocks.some((block) => block.type === 'banner'),
    [feedBlocks]
  );

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }

    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const updatePreference = () => {
      setPrefersReducedMotion(mediaQuery.matches);
    };

    updatePreference();

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', updatePreference);
      return () => mediaQuery.removeEventListener('change', updatePreference);
    }

    mediaQuery.addListener(updatePreference);
    return () => mediaQuery.removeListener(updatePreference);
  }, []);

  return (
    <main className="px-[16px] md:px-[40px] bg-light-secondary dark:bg-brand-dark min-h-screen transition-colors duration-200">
      <div className="max-w-[1400px] mx-auto pb-8 pt-[16px] md:pt-[24px]">
        <h1 className="text-[24px] md:text-[40px] font-bold mb-[16px] md:mb-[24px] text-light-text-primary dark:text-brand-text-primary tracking-tight">
          Catalog
        </h1>

        {loading ? (
          <div className="grid grid-cols-2 gap-[12px] md:grid-cols-2 md:gap-[20px] lg:grid-cols-4 lg:gap-[24px]">
            <SkeletonLoader variant="product-card" count={8} />
          </div>
        ) : error ? (
          <div className="max-w-[540px] mx-auto rounded-[18px] border border-[#F3C1C1] bg-[#FFF4F4] px-[20px] py-[24px] text-center">
            <p className="text-[16px] font-medium text-[#9F2D2D]">{error}</p>
            <button
              type="button"
              onClick={() => void loadProducts()}
              className="mt-[16px] inline-flex items-center justify-center rounded-[12px] bg-black px-[18px] py-[12px] text-[14px] font-semibold text-white transition hover:bg-black/90"
            >
              Try Again
            </button>
          </div>
        ) : hasEmptyCatalog ? (
          <p className="text-[16px] text-light-text-secondary dark:text-brand-text-secondary text-center py-[48px]">
            No products are available right now.
          </p>
        ) : (
          <CatalogImageRotationProvider
            hasRotatingCatalogImages={hasRotatingCatalogImages}
            hasAttentionLoop={hasAttentionLoop}
            prefersReducedMotion={prefersReducedMotion}
          >
            <div className="space-y-[20px] md:space-y-[28px]">
              {feedBlocks.map((block, blockIndex) => {
                switch (block.type) {
                  case 'product':
                    return (
                      <CatalogProductGrid
                        key={block.id}
                        products={block.data}
                        priorityImageCount={blockIndex === 0 ? 4 : 0}
                      />
                    );
                  case 'banner':
                    return (
                      <DynamicCatalogBanner
                        key={block.id}
                        sequenceIndex={block.sequenceIndex}
                      />
                    );
                  case 'lifestyle':
                    return <CatalogLifestyleBlock key={block.id} sequenceIndex={block.sequenceIndex} />;
                  case 'video':
                    return <CatalogLifestyleVideoSection key={block.id} />;
                  default:
                    return null;
                }
              })}
            </div>
          </CatalogImageRotationProvider>
        )}
      </div>
    </main>
  );
};

export default Catalog;
